// Central financial-period lock. A closed month is frozen: nothing that changes
// that month's P&L (invoice approvals / manual invoices, direct costs, operating
// expenses, petty cash, payroll) can be added, edited or reversed until the
// month is reopened. Corrections to a closed month are posted in the next open
// month instead.
//
// finance_period_locks/{YYYY-MM} = { status: "LOCKED" | "OPEN", ... }. The same
// rule is enforced in firestore.rules (financePeriodOpen), so it also holds
// for anything that bypasses these checks; the checks here exist to give a
// readable message before a write is even attempted.
import { collection, doc, getDoc, getDocs, serverTimestamp, writeBatch } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { lockedMessage, normalizePeriod } from "./periodLockLogic";

export { lockedMessage, normalizePeriod };

const LOCKS = "finance_period_locks";
const LOCK_LOG = "finance_period_lock_log";

export async function isPeriodLocked(period) {
  const key = normalizePeriod(period);
  if (!key) return false;
  const snapshot = await getDoc(doc(db, LOCKS, key));
  return snapshot.exists() && snapshot.data().status === "LOCKED";
}

export async function assertPeriodOpen(period) {
  const key = normalizePeriod(period);
  if (key && await isPeriodLocked(key)) throw new Error(lockedMessage(key));
}

export async function listPeriodLocks() {
  const result = await getDocs(collection(db, LOCKS));
  return result.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function setPeriodLock(period, locked, note = "") {
  const key = normalizePeriod(period);
  if (!key) throw new Error("Choose a month (YYYY-MM).");
  if (!locked && !String(note).trim()) throw new Error("Enter the reason for reopening this month.");
  const who = { uid: auth.currentUser?.uid || null, email: auth.currentUser?.email || null };
  const batch = writeBatch(db);
  batch.set(doc(db, LOCKS, key), {
    period: key,
    status: locked ? "LOCKED" : "OPEN",
    updatedByUid: who.uid,
    updatedByEmail: who.email,
    updatedAt: serverTimestamp()
  });
  batch.set(doc(collection(db, LOCK_LOG)), {
    period: key,
    action: locked ? "LOCKED" : "REOPENED",
    note: String(note || "").trim(),
    byUid: who.uid,
    byEmail: who.email,
    at: serverTimestamp()
  });
  await batch.commit();
}

// Only for deciding whether to show the Reopen button; the rules are the authority.
export const canReopenPeriods = () => String(auth.currentUser?.email || "").toLowerCase() === "admin@almhr.com";
